import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mensagemSenhaRepetida } from '@/lib/senhaFilaErro';
import { buscarSituacaoInscrito, type SituacaoInscrito } from '@/lib/situacaoInscrito';
import SeloSituacaoInscrito from '@/components/feature/SeloSituacaoInscrito';
import { ehPerguntaDoTermo, marcouFilhos } from '@/lib/filhosFicha';
import TextoRico from '@/components/base/TextoRico';
import CampoEdicao from './components/CampoEdicao';
import type { FeedbackCampo, FichaParaEditar, MetaInscricao, PerguntaEditar } from './types';

interface BlocoEdicao {
  secao: string | null;
  perguntas: PerguntaEditar[];
  /** Este bloco tinha alguma pergunta do termo (escondida nesta tela). */
  tinhaTermo?: boolean;
}

function agrupar(perguntas: PerguntaEditar[]): BlocoEdicao[] {
  const ordenadas = [...perguntas].sort((a, b) => a.ordem - b.ordem);
  const blocos: BlocoEdicao[] = [];
  let atual: BlocoEdicao | null = null;
  ordenadas.forEach((p) => {
    if (atual && atual.secao === p.secao) {
      atual.perguntas.push(p);
    } else {
      atual = { secao: p.secao, perguntas: [p] };
      blocos.push(atual);
    }
  });
  return blocos;
}

function formatarAte(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d
    .toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
    .replace(',', ' às');
}

export default function MinhaFichaPage() {
  const { codigo } = useParams<{ codigo: string }>();
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [naoEncontrado, setNaoEncontrado] = useState(false);

  const [meta, setMeta] = useState<MetaInscricao | null>(null);
  const [ficha, setFicha] = useState<FichaParaEditar | null>(null);
  const [situacao, setSituacao] = useState<SituacaoInscrito | null>(null);
  const [carregandoSituacao, setCarregandoSituacao] = useState(true);

  // Chave de cada pergunta, para esconder o termo das crianças sem mexer na ficha.
  const [chaves, setChaves] = useState<Record<number, string | null>>({});

  const [editando, setEditando] = useState(false);
  const [valores, setValores] = useState<Record<number, string>>({});
  const [feedbacks, setFeedbacks] = useState<Record<number, FeedbackCampo | null>>({});
  const [salvando, setSalvando] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      guardarDestino();
      navigate(
        `/entrar?voltar=${encodeURIComponent(`/minha-inscricao/${codigo}/ficha`)}`,
        { replace: true },
      );
      return;
    }
    let ativo = true;

    async function carregar() {
      setLoading(true);
      setErro(null);
      setNaoEncontrado(false);
      try {
        await garantirUsuario();

        const { data: inscritoData, error: errInscrito } = await supabase
          .from('vw_inscritos')
          .select('inscricao_id, evento, participante, modalidade, grupo, status')
          .eq('codigo', codigo)
          .maybeSingle();
        if (errInscrito) throw errInscrito;
        if (!inscritoData) {
          if (ativo) setNaoEncontrado(true);
          return;
        }

        const linha = inscritoData as MetaInscricao;
        if (ativo) setMeta(linha);

        // Sigilo: enquanto não responde, o selo fica neutro.
        if (ativo) setCarregandoSituacao(true);
        const sit = await buscarSituacaoInscrito(linha.inscricao_id).catch(() => null);
        if (ativo) {
          setSituacao(sit);
          setCarregandoSituacao(false);
        }

        const { data: fichaData, error: errFicha } = await supabase.rpc('ficha_para_editar', {
          p_inscricao: linha.inscricao_id,
        });
        if (errFicha) throw errFicha;

        const f = fichaData as FichaParaEditar;
        if (!f.ok) {
          if (ativo) {
            setErro(f.erro || 'Não foi possível abrir a ficha.');
          }
          return;
        }

        // Só as chaves interessam aqui: o termo das crianças não aparece nesta ficha.
        const ids = f.perguntas.map((p) => p.pergunta_id);
        const mapaChaves: Record<number, string | null> = {};
        if (ids.length > 0) {
          const { data: chavesData } = await supabase
            .from('perguntas')
            .select('id, chave')
            .in('id', ids);
          ((chavesData as { id: number; chave: string | null }[] | null) ?? []).forEach((p) => {
            mapaChaves[p.id] = p.chave;
          });
        }

        const inicial: Record<number, string> = {};
        f.perguntas.forEach((p) => {
          inicial[p.pergunta_id] = p.resposta ?? '';
        });
        if (ativo) {
          setChaves(mapaChaves);
          setFicha(f);
          setValores(inicial);
        }
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e, 'Não foi possível carregar a ficha.'));
      } finally {
        if (ativo) setLoading(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [codigo, navigate, pronto, user]);

  // O termo das crianças só é assinado pelo link dos pais: não entra na edição.
  const blocos = useMemo<BlocoEdicao[]>(() => {
    if (!ficha) return [];
    return agrupar(ficha.perguntas)
      .map((b) => ({
        secao: b.secao,
        perguntas: b.perguntas.filter((p) => !ehPerguntaDoTermo(chaves[p.pergunta_id])),
        tinhaTermo: b.perguntas.some((p) => ehPerguntaDoTermo(chaves[p.pergunta_id])),
      }))
      .filter((b) => b.perguntas.length > 0);
  }, [ficha, chaves]);

  const levaFilhos = useMemo(() => {
    if (!ficha) return false;
    return marcouFilhos(
      ficha.perguntas.map((p) => ({
        chave: chaves[p.pergunta_id] ?? null,
        valor: valores[p.pergunta_id],
      })),
    );
  }, [ficha, chaves, valores]);

  async function salvarResposta(perguntaId: number, novoValor: string) {
    if (!meta) return;
    const anterior = valores[perguntaId];

    setValores((prev) => ({ ...prev, [perguntaId]: novoValor }));
    setSalvando((prev) => ({ ...prev, [perguntaId]: true }));
    setFeedbacks((prev) => ({ ...prev, [perguntaId]: null }));

    try {
      const { data, error } = await supabase.rpc('salvar_resposta', {
        p_inscricao: meta.inscricao_id,
        p_pergunta: perguntaId,
        p_valor: novoValor,
      });
      if (error) throw error;

      const r = data as { ok: boolean; recado?: string; erro?: string };
      if (r.ok) {
        setFeedbacks((prev) => ({
          ...prev,
          [perguntaId]: { tipo: 'recado', texto: r.recado || 'Salvo.' },
        }));
      } else {
        setFeedbacks((prev) => ({
          ...prev,
          [perguntaId]: { tipo: 'erro', texto: r.erro || 'Não foi possível salvar.' },
        }));
        setValores((prev) => ({ ...prev, [perguntaId]: anterior }));
      }
    } catch (e) {
      // Senha da fila repetida: mostra o texto do banco, sem trocar por
      // "não foi possível salvar".
      setFeedbacks((prev) => ({
        ...prev,
        [perguntaId]: {
          tipo: 'erro',
          texto: mensagemSenhaRepetida(e) ?? mensagemAmigavel(e),
        },
      }));
      setValores((prev) => ({ ...prev, [perguntaId]: anterior }));
    } finally {
      setSalvando((prev) => ({ ...prev, [perguntaId]: false }));
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-4 py-8">
          <div className="h-6 w-56 animate-pulse rounded bg-secondary-100" />
          <div className="mt-2 h-4 w-40 animate-pulse rounded bg-secondary-100" />
          <div className="mt-6 h-32 animate-pulse rounded-lg bg-secondary-100" />
          <div className="mt-4 h-32 animate-pulse rounded-lg bg-secondary-100" />
          <div className="mt-4 h-32 animate-pulse rounded-lg bg-secondary-100" />
        </div>
      </div>
    );
  }

  if (naoEncontrado) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-6 text-center">
            <h1 className="text-xl font-normal text-foreground-950">Ficha não encontrada</h1>
            <p className="mt-2 text-sm text-secondary-500">
              Não encontramos esta inscrição ou esta ficha.
            </p>
            <button
              type="button"
              onClick={() => navigate('/minhas-inscricoes')}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Ver minhas inscrições
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (erro || !ficha || !meta) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-6 text-center">
            <h1 className="text-xl font-normal text-foreground-950">Ficha fechada</h1>
            <p className="mt-2 text-sm text-foreground-600">{erro}</p>
            <button
              type="button"
              onClick={() => navigate(`/minha-inscricao/${codigo}`)}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Voltar à inscrição
            </button>
          </div>
        </div>
      </div>
    );
  }

  const participacao = meta.grupo ? `${meta.modalidade} — ${meta.grupo}` : meta.modalidade;

  return (
    <div className="min-h-screen bg-background-100">
      <header className="sticky top-0 z-20 border-b border-secondary-200 bg-white">
        <div className="mx-auto w-full max-w-[720px] px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/minha-inscricao/${codigo}`)}
              className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-secondary-600 transition-colors hover:bg-background-100"
              aria-label="Voltar"
            >
              <i className="ri-arrow-left-line text-xl" />
            </button>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground-950">{meta.participante}</p>
              <p className="truncate text-xs text-secondary-500">{meta.evento}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[720px] px-4 py-6">
        <h1 className="text-2xl font-normal text-foreground-950">Minha ficha</h1>
        <p className="mt-1 text-sm text-secondary-500">
          {participacao}
          {ficha.de_quem ? ` · inscrita por ${ficha.de_quem}` : ''}
        </p>

        <SeloSituacaoInscrito
          situacao={situacao}
          carregando={carregandoSituacao}
          comTexto={situacao?.sigilo === true || situacao?.situacao === 'lista_espera'}
          className="mt-3"
        />

        {!ficha.pode_editar && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-800">{ficha.motivo}</p>
          </div>
        )}

        {ficha.pode_editar && (
          <div className="mt-4 rounded-lg border border-secondary-200 bg-white px-4 py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium text-foreground-950">
                  {editando ? 'Você está corrigindo a ficha.' : 'Precisa corrigir algum dado?'}
                </p>
                {ficha.aberta_ate && (
                  <p className="mt-0.5 text-xs text-secondary-500">
                    Aberta até {formatarAte(ficha.aberta_ate)}.
                  </p>
                )}
              </div>
              {!editando ? (
                <button
                  type="button"
                  onClick={() => setEditando(true)}
                  className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-edit-line" />
                  </span>
                  Editar minha ficha
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditando(false)}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                >
                  Concluir
                </button>
              )}
            </div>
            {editando && (
              <p className="mt-2 text-xs text-secondary-500">
                Cada resposta é salva sozinha quando você sai do campo. Se algo deu errado, o campo
                volta ao que estava.
              </p>
            )}
          </div>
        )}

        <div className="mt-4 space-y-4">
          {blocos.map((bloco, idx) => (
            <div key={`${bloco.secao ?? 'sem-secao'}-${idx}`}>
              {bloco.secao && (
                <h2 className="mb-2 px-1 text-sm font-medium text-secondary-600">{bloco.secao}</h2>
              )}
              <div className="space-y-4">
                {bloco.perguntas.map((p) => (
                  <div key={p.pergunta_id} className="rounded-lg border border-secondary-200 bg-white p-6">
                    <div className="flex flex-wrap items-baseline gap-x-1 text-base font-medium text-foreground-950">
                      <TextoRico texto={p.enunciado} />
                      {p.obrigatoria && <span className="text-danger-600">*</span>}
                    </div>
                    {p.descricao && (
                      <TextoRico texto={p.descricao} className="mt-1 text-sm text-secondary-500" />
                    )}
                    <div className="mt-3">
                      <CampoEdicao
                        pergunta={p}
                        editando={editando}
                        valor={valores[p.pergunta_id] ?? ''}
                        feedback={feedbacks[p.pergunta_id] ?? null}
                        salvando={salvando[p.pergunta_id] ?? false}
                        aoSalvar={(novo) => salvarResposta(p.pergunta_id, novo)}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {bloco.tinhaTermo && levaFilhos && (
                <p className="mt-3 rounded-lg bg-secondary-50 px-4 py-3 text-[13px] leading-relaxed text-secondary-700">
                  Os dados de saúde e a autorização das crianças são preenchidos pelo link que a
                  direção vai mandar.
                </p>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}